import { useEffect } from 'react';
import { getContext } from '@/context';
import { useAppStore } from '@/store';
import { InputType } from '@/types';
import { getPickerResourceUrl } from '@/utils/picker/pickerUtils';

import type { IAppStore } from '@/store';
import type { IImageInput, IVideoInput, PickerResource } from '@/types';

const selector = (store: IAppStore) => ({
  inputs: store.inputs,
  updateInput: store.updateInput,
});

/**
 * Handles the edge case where the miniapp is unloaded from memory during
 * the native picker session. When the app reloads, this hook receives
 * the selected resources and updates the first available input.
 *
 * This is a native mobile-only scenario - the SDK's onChooserResponse
 * callback fires when the user selected files but the miniapp was
 * killed by the OS during the selection process.
 */
const useChooserResponse = (): void => {
  const { inputs, updateInput } = useAppStore(selector);

  useEffect(() => {
    try {
      const context = getContext();

      // Subscribe to chooser response (fires after app reload during picker session)
      const unsubscribe = context.handlers.onChooserResponse((resources) => {
        if (!resources || resources.length === 0) {
          return;
        }

        // eslint-disable-next-line no-console
        console.log(
          '[useChooserResponse] Received resources after reload:',
          resources
        );

        // Find the first empty media input to fill
        const emptyMediaInput = inputs.find(
          (input) =>
            (input.type === InputType.IMAGE ||
              input.type === InputType.VIDEO) &&
            !input.src
        );

        if (!emptyMediaInput) {
          // eslint-disable-next-line no-console
          console.log('[useChooserResponse] No empty media input found');
          return;
        }

        // Get the URL from the first resource
        const resource = resources[0] as PickerResource;
        const url = getPickerResourceUrl(resource);

        if (url) {
          // eslint-disable-next-line no-console
          console.log(
            '[useChooserResponse] Updating input:',
            emptyMediaInput.id,
            'with URL:',
            url
          );
          updateInput({
            id: emptyMediaInput.id,
            value: { src: url } as Partial<IImageInput | IVideoInput>,
          });
        }
      });

      return () => {
        unsubscribe?.();
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[useChooserResponse] Failed to subscribe:', error);
    }
  }, [inputs, updateInput]);
};

export default useChooserResponse;
